/* Real gateway implementation of AnalyticsClient.
 *
 * Talks to the v1 Analytics API over HTTP. Endpoints map 1:1 to the contract.
 * The UI extension the contract defers is handled here so the UI behaves
 * identically on the mock or the live gateway: getOrgStructure returns an
 * empty org (team/repo attribution is deferred server-side, with no endpoint
 * to source it from). */

import { authFetchInit } from "../auth/fetch";
// Direct module import (not the ../identity barrel) to avoid an import cycle.
import { workspaceHeader } from "../identity/active";
import type { AnalyticsClient } from "./AnalyticsClient";
import type {
  AgentsResponse,
  AnalyticsQuery,
  ApiError,
  ApiErrorCode,
  ErrorBreakdownResponse,
  ErrorsQuery,
  ErrorsResponse,
  ModelsResponse,
  OrgStructure,
  SessionDetailResponse,
  SessionsQuery,
  SessionsResponse,
  SummaryResponse,
  TransformersResponse,
  TrendBucket,
  TrendsResponse,
} from "./types";

const ORG_NAME = "AI21";

// Filters the live PG backend accepts. As of contract v1.2 (§9b) config_id,
// agent_id and agent_kind are supported (the columns exist); experiment and
// repo still 400/422 on the PG backend, so they stay omitted here — a stale URL
// param (e.g. ?repo=…) must not fail every request. Add them when BigQuery ships.
const SUPPORTED_FILTERS = [
  "model",
  "session_id",
  "config_id",
  "agent_id",
  "agent_kind",
] as const;

// The failure drill-down filters on two dimensions the aggregate endpoints have
// no column for (OPSS-352, BFF `ERRORS_APPLIED`). They extend the whitelist for
// the /errors pair only — sending them to /summary or /agents, which don't
// declare them, is the class of param error that comes back as a bare 400 and
// is then read as an auth failure (see `request` below).
const ERROR_FILTERS = ["status", "provider"] as const;

function buildParams(query: AnalyticsQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (query.from) p.set("from", query.from);

  if (query.to) p.set("to", query.to);

  for (const key of SUPPORTED_FILTERS) {
    for (const v of query[key] ?? []) p.append(key, v);
  }
  if (query.routed !== undefined) p.set("routed", String(query.routed));

  return p;
}

/** Shared params plus the errors-only status/provider filters. */
function buildErrorParams(query: ErrorsQuery): URLSearchParams {
  const p = buildParams(query);
  for (const key of ERROR_FILTERS) {
    for (const v of query[key] ?? []) p.append(key, String(v));
  }
  return p;
}

export class HttpAnalyticsClient implements AnalyticsClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;

  constructor(baseUrl: string, apiKey?: string) {
    // `baseUrl` is the gateway mount root (e.g. "/coding-gateway/internal" or
    // "https://api.ai21.com/tokenwise"); the analytics API is the `/analytics/v1`
    // sub-path under it — a sibling of the `/settings/*` CRUD. Append it here so
    // `${baseUrl}/summary` becomes "${root}/analytics/v1/summary".
    this.baseUrl = `${baseUrl.replace(/\/+$/, "")}/analytics/v1`;
    this.apiKey = apiKey;
  }

  private async request<T>(path: string, params?: URLSearchParams): Promise<T> {
    const qs = params && [...params.keys()].length ? `?${params}` : "";
    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}${path}${qs}`, {
        // When the auth gate is on, send the identity-service session cookie.
        ...authFetchInit(),
        // Auth is open in local dev; prod sets ANALYTICS_API_KEY and expects a
        // Bearer token on every request (UI_CHANGELOG #7).
        headers: {
          Accept: "application/json",
          ...(this.apiKey ? { Authorization: `Bearer ${this.apiKey}` } : {}),
          ...workspaceHeader(),
        },
      });
    } catch (cause) {
      const error: ApiError = {
        code: "upstream",
        message: "Couldn't reach the analytics service.",
        detail: String(cause),
      };
      throw { error };
    }
    if (!res.ok) {
      // Contract: non-2xx bodies are { error: ApiError }.
      const body = await res.json().catch(() => null);
      if (body && typeof body === "object" && "error" in body) throw body;

      // Auth rejections arrive as a bare framework error, NOT the analytics
      // `{ error }` envelope — e.g. the api-server returns FastAPI's
      // `{ "detail": ... }` with 401 when identity rejects the session. Map
      // those to the auth codes so the UI can prompt a re-login; mislabelling
      // them "upstream" is what surfaced an expired session as
      // "Couldn't reach the analytics service."
      //
      // A non-enveloped 400 here is the api-server's "bad or missing
      // authentication" (no session cookie reached it) — an auth problem, not
      // a client/param error. Genuine param errors arrive enveloped as
      // `bad_request` (thrown above), so treating a bare 400 as `unauthorized`
      // is safe and lets the UI prompt a re-login (which re-sends the cookie).
      const code: ApiErrorCode =
        res.status === 401 || res.status === 400
          ? "unauthorized"
          : res.status === 403
            ? "forbidden"
            : res.status === 404
              ? "not_found"
              : "upstream";
      const error: ApiError = {
        code,
        message: `Request to ${path} failed (HTTP ${res.status}).`,
      };
      throw { error };
    }

    return (await res.json()) as T;
  }

  getSummary(query: AnalyticsQuery): Promise<SummaryResponse> {
    return this.request("/summary", buildParams(query));
  }

  getSessions(query: SessionsQuery): Promise<SessionsResponse> {
    const p = buildParams(query);
    if (query.limit !== undefined) p.set("limit", String(query.limit));

    if (query.cursor) p.set("cursor", query.cursor);

    if (query.sort) p.set("sort", query.sort);

    if (query.order) p.set("order", query.order);

    return this.request("/sessions", p);
  }

  getErrors(query: ErrorsQuery): Promise<ErrorsResponse> {
    const p = buildErrorParams(query);
    if (query.limit !== undefined) p.set("limit", String(query.limit));

    if (query.cursor) p.set("cursor", query.cursor);

    return this.request("/errors", p);
  }

  getErrorBreakdown(query: ErrorsQuery): Promise<ErrorBreakdownResponse> {
    // Aggregate over the same window and filters as getErrors, so the panel's
    // breakdown and its per-call table can never disagree. No limit/cursor: the
    // BFF caps each dimension server-side.
    return this.request("/errors/breakdown", buildErrorParams(query));
  }

  getSession(
    sessionId: string,
    query: AnalyticsQuery = {},
  ): Promise<SessionDetailResponse> {
    return this.request(
      `/sessions/${encodeURIComponent(sessionId)}`,
      buildParams(query),
    );
  }

  getTransformers(query: AnalyticsQuery): Promise<TransformersResponse> {
    return this.request("/transformers", buildParams(query));
  }

  getTrends(
    query: AnalyticsQuery & { bucket?: TrendBucket },
  ): Promise<TrendsResponse> {
    const p = buildParams(query);
    if (query.bucket) p.set("bucket", query.bucket);

    return this.request("/trends", p);
  }

  getAgents(query: AnalyticsQuery): Promise<AgentsResponse> {
    return this.request("/agents", buildParams(query));
  }

  getModels(query: AnalyticsQuery): Promise<ModelsResponse> {
    return this.request("/models", buildParams(query));
  }

  // ---- derived extensions (not in v1; see types.ts) ------------------------
  getOrgStructure(): Promise<OrgStructure> {
    // Team/repo attribution is deferred server-side and there is no cheap
    // endpoint to source it from (it used to be derived from /filters' `repos`,
    // which the gateway never populated). Return an empty org so the scope
    // switcher — currently unsurfaced — keeps the scope at Org until real
    // attribution ships.
    return Promise.resolve({ org: ORG_NAME, teams: [] });
  }
}
